import { ApolloClient, useApolloClient, useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Merge from "@mui/icons-material/Merge";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { gql } from "generated-graphql";
import { Permission, TenantCatalogChemicalFragment } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import pluralize from "pluralize";
import { useState } from "react";

import { ChemicalMergePicker } from "./ChemicalMergePicker";

const BULK_VERIFY_CHEMICALS_MUTATION = gql(`
    mutation BulkVerifyChemicals($chemicalIds: [ID!]!) {
        verifyChemicals(chemicalIds: $chemicalIds)
    }
`);

const BULK_UNVERIFY_CHEMICALS_MUTATION = gql(`
     mutation BulkUnverifyChemicals($chemicalIds: [ID!]!) {
        unverifyChemicals(chemicalIds: $chemicalIds)
    }`);

// Optimistically update cache: set needsReview to false for verified chemicals
function markChemicalsVerifiedInCache(client: ApolloClient<object>, chemicalIds: string[]) {
  for (const chemicalId of chemicalIds) {
    client.cache.modify({
      id: client.cache.identify({ __typename: "Chemical", id: chemicalId }),
      fields: {
        needsReview: () => false,
        hazardReviewReasons: () => [],
        verifiedAt: () => new Date().toISOString(),
      },
    });
  }
}

// The menu items stay enabled and mark unavailability with `aria-disabled`
// (the WAI-ARIA pattern for disabled menu items): MUI's `disabled` prop sets
// pointer-events: none, which suppresses the explanatory tooltip, and the
// span-wrapper workaround for that breaks MenuList keyboard navigation, which
// only walks focusable direct children. Callers must guard onClick themselves.
const disabledLookSx: SxProps<Theme> = {
  opacity: (theme) => theme.palette.action.disabledOpacity,
};

// Update cache: clear verification fields
function markChemicalsUnverifiedInCache(client: ApolloClient<object>, chemicalIds: string[]) {
  for (const chemicalId of chemicalIds) {
    client.cache.modify({
      id: client.cache.identify({ __typename: "Chemical", id: chemicalId }),
      fields: {
        needsReview: () => true,
        verifiedAt: () => null,
        verifiedBy: () => null,
      },
    });
  }
}

export const ChemicalCatalogBulkActions = function ({
  chemicals,
}: {
  chemicals: TenantCatalogChemicalFragment[];
}) {
  const buttonId = "chemical-catalog-bulk-actions";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setDialogOpen] = useState<undefined | "verify" | "unverify">(undefined);
  const [mergeOpen, setMergeOpen] = useState(false);
  const alerts = useAlerts();
  const client = useApolloClient();
  const { hasPermissions } = useAuthorization();
  const canMerge = hasPermissions([Permission.MergeChemicals]);
  const chemicalIds = chemicals.map((chemical) => chemical.id);
  const allWritable = chemicals.every((chemical) => chemical.canWrite);
  const notWritableTooltip = allWritable
    ? ""
    : "Your selection includes chemicals you do not have permission to modify";
  // Merging also deletes the source chemicals.
  const allMergeable = chemicals.every((chemical) => chemical.canWrite && chemical.canDelete);
  const notMergeableTooltip = allMergeable
    ? ""
    : "Your selection includes chemicals you do not have permission to modify or delete";
  const verifyDisabled = chemicals.length === 0 || !allWritable;
  const mergeDisabled = chemicalIds.length < 2 || !allMergeable;

  const [bulkVerifyChemicals, { loading: bulkVerifyingChemicals }] = useMutation(
    BULK_VERIFY_CHEMICALS_MUTATION
  );

  const [bulkUnverifyChemicals, { loading: bulkUnverifyingChemicals }] = useMutation(
    BULK_UNVERIFY_CHEMICALS_MUTATION
  );

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        size="medium"
        disabled={chemicals.length === 0}
      >
        Bulk Actions
      </Button>
      <Menu
        id="add-chemical-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": buttonId,
          dense: true,
        }}
      >
        <Tooltip key="bulk-verify" title={notWritableTooltip} placement="left" describeChild>
          <MenuItem
            aria-disabled={verifyDisabled || undefined}
            sx={verifyDisabled ? disabledLookSx : undefined}
            onClick={() => {
              if (verifyDisabled) return;
              setDialogOpen("verify");
              handleClose();
            }}
          >
            <ListItemIcon>
              <Check />
            </ListItemIcon>
            <ListItemText>Verify Chemicals</ListItemText>
          </MenuItem>
        </Tooltip>
        <Tooltip key="bulk-unverify" title={notWritableTooltip} placement="left" describeChild>
          <MenuItem
            aria-disabled={verifyDisabled || undefined}
            sx={verifyDisabled ? disabledLookSx : undefined}
            onClick={() => {
              if (verifyDisabled) return;
              setDialogOpen("unverify");
              handleClose();
            }}
          >
            <ListItemIcon>
              <Close />
            </ListItemIcon>
            <ListItemText>Unverify Chemicals</ListItemText>
          </MenuItem>
        </Tooltip>
        {canMerge && (
          <Tooltip key="merge" title={notMergeableTooltip} placement="left" describeChild>
            <MenuItem
              aria-disabled={mergeDisabled || undefined}
              sx={mergeDisabled ? disabledLookSx : undefined}
              onClick={() => {
                if (mergeDisabled) return;
                setMergeOpen(true);
                handleClose();
              }}
            >
              <ListItemIcon>
                <Merge />
              </ListItemIcon>
              <ListItemText>Merge Chemicals</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
      </Menu>
      {mergeOpen && (
        <ChemicalMergePicker open onClose={() => setMergeOpen(false)} chemicalIds={chemicalIds} />
      )}
      <ConfirmDialog
        open={!!openDialog}
        onClose={() => setDialogOpen(undefined)}
        loading={bulkVerifyingChemicals || bulkUnverifyingChemicals}
        onConfirm={async () => {
          try {
            if (openDialog === "verify") {
              await bulkVerifyChemicals({
                variables: { chemicalIds },
              });
              markChemicalsVerifiedInCache(client, chemicalIds);
            } else {
              await bulkUnverifyChemicals({
                variables: { chemicalIds },
              });
              markChemicalsUnverifiedInCache(client, chemicalIds);
            }
            alerts.success(
              `Successfully ${openDialog === "verify" ? "verified" : "unverified"} chemicals.`
            );
          } catch (err) {
            alerts.error(
              `There was an error ${
                openDialog === "verify" ? "verifying" : "unverifying"
              } chemicals`,
              { err }
            );
          }
          setDialogOpen(undefined);
        }}
        header={`Are you sure you want to proceed to ${openDialog}?`}
        confirmText={`Yes, ${openDialog}`}
        info={`Are you sure you want to ${openDialog} the selected ${chemicals.length} ${pluralize(
          "chemical",
          chemicals.length
        )}? This indicates that these chemicals in Encamp ${
          openDialog === "verify" ? "" : "do not"
        } match their SDS documents. Other users of your organization will see these chemicals as ${
          openDialog === "verify" ? "verified" : "unverified"
        }.`}
      />
    </>
  );
};
